<template>
  <div class="b-modal">
    <div
      v-if="loading"
      class="text-center"
    >
      <div class="spinner-grow text-primary" />
    </div>
    <div
      v-if="submitted"
      class="text"
    >
      <p data-i18n="waitinglistSubmitted">
        {{ $t('waitinglistSubmitted') }}
      </p>
      <button
        class="btn btn-primary"
        data-i18n="close"
        @click="$emit('close')"
      >
        {{ $t('close') }}
      </button>
    </div>
    <form
      v-if="!loading && !submitted"
      @submit.prevent="add"
    >
      <div
        v-if="error"
        class="alert alert-danger"
        data-i18n="bookRoomWaitinglistError"
      >
        {{ $t('bookRoomWaitinglistError') }}
      </div>
      <p data-i18n="waitinglistInstructions">
        {{ $t('waitinglistInstructions') }}
      </p>
      <div class="form-group">
        <label
          for="waiting-name"
          data-i18n="nameLabel"
        >{{ $t('nameLabel') }}</label>
        <input
          id="waiting-name"
          v-model="name"
          class="form-control"
          autocomplete="name"
          :class="{ 'is-invalid': v$.name.$error }"
        />
        <div
          v-if="v$.name.$error"
          class="w-12/12 invalid-feedback"
          data-i18n="formFieldRequiredError"
        >
          {{ $t('formFieldRequiredError') }}
        </div>
      </div>
      <div class="form-group">
        <label
          for="waiting-phone"
          data-i18n="phoneLabel"
        >{{ $t('phoneLabel') }}</label>
        <input
          id="waiting-phone"
          v-model="phone"
          v-maska
          class="form-control"
          :masked="true"
          data-maska="################"
          autocomplete="tel"
          :class="{ 'is-invalid': v$.phone.$error }"
        />
        <div
          v-if="v$.phone.$error"
          class="w-12/12 invalid-feedback"
          data-i18n="formFieldRequiredError"
        >
          {{ $t('formFieldRequiredError') }}
        </div>
      </div>
      <div class="form-group">
        <label
          for="waiting-email"
          data-i18n="formEmail"
        >{{ $t('formEmail') }}</label>
        <input
          id="waiting-email"
          v-model="email"
          class="form-control"
          type="email"
          autocomplete="email"
          :class="{ 'is-invalid': v$.email.$error }"
        />
        <div
          v-if="v$.email.$error"
          class="w-12/12 invalid-feedback"
        >
          <template v-if="!v$.email.required">
            <span data-i18n="formFieldRequiredError">
              {{ $t('formFieldRequiredError') }}
            </span>
          </template>
          <template v-else-if="!v$.email.email">
            <span data-i18n="formFieldInvalidEmail">
              {{ $t('formFieldInvalidEmail') }}
            </span>
          </template>
        </div>
      </div>
      <div
        v-show="different_rooms_possible"
        class="form-group"
      >
        <label
          for="waiting-room"
          data-i18n="roomLabel"
        >{{ $t('roomLabel') }}</label>
        <select
          id="waiting-room"
          v-model="room"
          class="form-control"
        >
          <option
            v-for="r in roomtypes"
            :key="r.id"
            :value="r.id"
          >
            {{ r.name }}
          </option>
        </select>
      </div>
      <div class="form-group">
        <label
          for="waiting-people"
          data-i18n="peopleLabel"
        >{{ $t('peopleLabel') }}</label>
        <select
          id="waiting-people"
          v-model="people"
          class="form-control"
        >
          <option
            v-for="r in [1, 2, 3]"
            :key="r"
            :value="r"
          >
            {{ r }}
          </option>
        </select>
      </div>
      <div class="form-group">
        <button
          class="btn btn-blue"
          data-i18n="addToWaitinglistButton"
        >
          {{ $t('addToWaitinglistButton') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { useVuelidate } from '@vuelidate/core'
import * as validators from '@vuelidate/validators'

const { trip } = defineProps({
  trip: {
    type: Object,
    required: true,
  },
})

defineEmits(['close'])

const localeStore = useLocaleStore()

const error = ref(false)
const loading = ref(true)
const submitted = ref(false)
const different_rooms_possible = ref(false)
const roomtypes = ref([])

const name = ref('')
const email = ref('')
const phone = ref('')
const people = ref(1)
const room = ref(null)

const v$ = useVuelidate({
  name: {
    required: validators.required,
  },

  phone: {
    required: validators.requiredIf(m => !m.email),
  },

  email: {
    email: validators.email,
    required: validators.requiredIf(m => !m.phone),
  },
}, { name, phone, email })

onBeforeMount(async () => {
  const { data } = await apiFetch(`/${localeStore.locale}/waitinglist-precond/${trip.tripid}`)

  loading.value = false
  different_rooms_possible.value = data.different_rooms_possible
  roomtypes.value = data.roomtypes

  if (roomtypes.value) {
    room.value = roomtypes.value[0].id
  }
})

const add = async () => {
  v$.value.$touch()

  if (v$.value.$invalid) {
    return
  }

  loading.value = true

  try {
    await apiFetch(`/${localeStore.locale}/waitinglist-add/${trip.tripid}`, {
      method: 'POST',
      body: {
        pax: people.value,
        name: name.value,
        phone: phone.value,
        email: email.value,
        roomtype: room.value,
      },
    })

    loading.value = false
    submitted.value = true
  } catch (e) {
    loading.value = false
    error.value = true
  }
}
</script>
